import classNames from "classnames";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import useTranslationModules from "../../../../../i18n/useTranslationModules";
import { EventsState } from "../../../../../store/types";
import { getDefaultAgendaImage } from "../../../../../store/utils/create-event";
import { TemplateClassNames } from "../../../../../types/template-layouts";
import { Session, SessionTypesFeatures, EventSessionTabs, NavMainItems, IHomepageMainNavItems, EPreviewTypes, SessionPreview, PageModuleType } from "../../../../../types/working-model";
import { capitalize } from "../../../../../utils/utils";
import SessionCard from "../../../../live-event/modules/agenda/agenda-session-card";
import { SessionFilter } from "../../../../live-event/modules/agenda/session-filter";
import AgendaPreviewModal from "../editor/preview/agenda-preview";
import { PostRegisterSideBar } from "./main-preview";
import './session-items-list.scss';

interface IEventAgendaItemsProps {
	eventMainNavItems: IHomepageMainNavItems[];
}

const SessionItemsList: React.FC<IEventAgendaItemsProps> = ({ eventMainNavItems }) => {
	const {
		CreateEventReducer: {
			workingEvent,
			previewMode,
		},
	} = useSelector((state: EventsState) => state);

	const { tab }: { tab: string; } = useParams();
	const { t } = useTranslation("homepage");

	const language = "en"; //locked to english for now because the post registered view does not currently have a language dropdown, when this changes, language will need to come from props

	useTranslationModules({ language });

	const activeTab = useMemo(() => {
		return tab && capitalize(tab).replaceAll("-", " ");
	}, [tab]);

	const template = TemplateClassNames[workingEvent?.template.name ?? 'Apollo'];
	const logo = workingEvent?.homepage?.header.logo;
	// get all sessions that have start time
	const [timedSessions, onDemandSessions] = useMemo(() => {
		if (workingEvent?.sessions) {
			let timedSessionsArray: Session[] = [];
			let onDemandSessionsArray: Session[] = [];
			for (const session of workingEvent.sessions) {
				if (SessionTypesFeatures[session.session_type].has_start_time) {
					timedSessionsArray = [...timedSessionsArray, session];
				} else {
					onDemandSessionsArray = [...onDemandSessionsArray, session];
				}
			}
			return [timedSessionsArray, onDemandSessionsArray];
		} else {
			return [[], []];
		}
	}, [workingEvent?.sessions]);

	const renderHeader = () => {
		// if both, then render tabs and show the currently active one
		if (timedSessions.length && onDemandSessions.length) {
			return (
				<div>
					<h6>Sessions</h6>
					<div className="preview-session-list-tabs">
						<div className={classNames({ 'active-tab': activeTab === EventSessionTabs.EventAgenda })}>Broadcasted</div>
						<div className={classNames({ 'active-tab': activeTab === EventSessionTabs.OnDemand })}>On-demand</div>
					</div>
				</div>
			);
		}
		// else don't show tabs
		return (
			<h6>{t("Agenda")}</h6>
		);
	};


	const agenda = workingEvent?.homepage?.modules.find(m => m.type === PageModuleType.agenda);
	const renderSearch = () => {
		// if event agenda, show full search filters
		if (activeTab === EventSessionTabs.EventAgenda && workingEvent?.sessions && agenda) {
			const forcingType = workingEvent.sessions as unknown;
			const _sessions = forcingType as SessionPreview[];
			//this is not actually being used in session filter as it is currently decorative
			return (
				<div className="agenda-sessions-header">
					<SessionFilter
						isLive={true}
						module={agenda}
						sessions={_sessions}
						template={template}
						onFilterChange={() => null}
					/>
				</div>
			);
		}
	};

	const sortedTimedSessions = useMemo(() => () => {
		if (timedSessions.length) {
			const sortedByDates = timedSessions.sort((a, b) => {
				if (a.timestamp && b.timestamp) {
					return a.timestamp - b.timestamp;
				}
				return 0;
			});
			return sortedByDates;
		}
		return [];
	}, [timedSessions]);

	const sessionCard = (session: Session, type: EventSessionTabs, idx: number) => {
		if (type === EventSessionTabs.EventAgenda && !session.timestamp) return null;
		// stylingOverrides not being applied in this instance of SessionCard
		return <SessionCard {...{ session, template, defaultImage: getDefaultAgendaImage(template, idx) }} />;
	};

	const cardStyling = template === TemplateClassNames.O2 ? 'agenda-vertical_card' : 'agenda-standard';
	if (!workingEvent?.sessions.length) return null;
	return (
		<>
			<PostRegisterSideBar {...{ eventMainNavItems, template, logo }} activeMenuItem={NavMainItems.Sessions} />
			<div className={classNames("preview-session-list-container event-agenda agenda", cardStyling, template)}>
				<div className={classNames("preview-session-list-header", {
					'multi-session-types-header': timedSessions.length && onDemandSessions.length
				})}>
					<div className="header-content">
						{renderHeader()}
					</div>
				</div>
				<div className={classNames("preview-session-list-content", template)}>
					{renderSearch()}
					<div className="agenda-sessions-list">
						{activeTab === EventSessionTabs.EventAgenda ? sortedTimedSessions().map((session, idx) => {

							return <React.Fragment key={session.session}>{sessionCard(session, EventSessionTabs.EventAgenda, idx)}</React.Fragment>;
						}) : null}
						{activeTab === EventSessionTabs.OnDemand ? onDemandSessions.map((session, idx) => {
							return <React.Fragment key={session.session}>{sessionCard(session, EventSessionTabs.OnDemand, idx)}</React.Fragment>;
						}) : null}
					</div>
				</div>
			</div>
			<AgendaPreviewModal open={previewMode === EPreviewTypes.Agenda} />
		</>
	);
};

export default SessionItemsList;
